import type { KeyboardEvent } from 'react'
import sendIcon from '@/assets/copilot/send.svg'
import { useUiStrings } from '@/hooks/useUiStrings'

type CopilotInputBoxProps = {
  searchText: string
  onSearchTextChange: (value: string) => void
  onSubmit: () => void
  disabled?: boolean
}

export default function CopilotInputBox({
  searchText,
  onSearchTextChange,
  onSubmit,
  disabled = false,
}: CopilotInputBoxProps) {
  const { t } = useUiStrings()
  const canSend = searchText.trim().length > 0 && !disabled

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      if (canSend) onSubmit()
    }
  }

  return (
    <div className="shrink-0 border-t border-[#e2e8f0] bg-white px-3 pb-3 pt-[13px] dark:border-[var(--border-color)] dark:bg-[var(--bg-secondary)]">
      <div className="relative">
        <div className="flex h-9 w-full items-center rounded-lg border border-[#e2e8f0] bg-white px-[13px] focus-within:border-[#2563eb] dark:border-[var(--border-color)] dark:bg-[var(--bg-tertiary)]">
          <input
            type="text"
            value={searchText}
            onChange={(event) => onSearchTextChange(event.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t.copilotInputPlaceholder}
            disabled={disabled}
            className="min-w-0 flex-1 border-none bg-transparent pr-8 text-xs text-[#0f172a] outline-none placeholder:text-[#94a3b8] disabled:opacity-50 dark:text-[var(--text-primary)]"
            data-testid="copilot-input"
          />
        </div>
        <button
          type="button"
          disabled={!canSend}
          onClick={onSubmit}
          aria-label={t.copilotLabel}
          className={[
            'absolute right-3 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-md shadow-[0px_1px_1px_rgba(0,0,0,0.05)] transition-colors',
            canSend ? 'bg-[#2563eb]' : 'bg-[#d9d9d9]',
          ].join(' ')}
          data-testid="copilot-send"
        >
          <img src={sendIcon} alt="" width={14} height={14} className="block h-[14px] w-[14px]" />
        </button>
      </div>
    </div>
  )
}
